/**
 * Button Component Tokens
 *
 * Severity mapping:
 * - primary: Primary filled button
 * - secondary: Outlined button
 * - success: Success/confirmation button
 * - contrast: Menu button (dark variant)
 * - tertiary: Text button (m-button type="tertiary" → p-button-text)
 * - text.primary: PrimeNG text-variant tokens (kept in sync with tertiary)
 */
export const buttonTokens = {
  button: {
    root: {
      borderRadius: '{borderRadius.sm}',
      roundedBorderRadius: '{borderRadius.full}',
      fontSize: '0.875rem',
      lineHeight: '1.38rem',
      gap: '0.25rem',
      paddingX: '0.75rem',
      paddingY: '0.25rem',
      minWidth: '4.3125rem',
      iconOnlyWidth: '2.5rem',
      transitionDuration: '{transition.duration}',
      focusRing: {
        width: '{focusRing.width}',
        style: '{focusRing.style}',
        color: '{focusRing.color}',
        offset: '{focusRing.offset}',
        shadow: '{focusRing.shadow}'
      }
    },

    colorScheme: {
      light: {
        root: {
          // Primary
          primary: {
            background: '{primary.color}',
            hoverBackground: '{primary.hoverColor}',
            activeBackground: '{primary.activeColor}',
            borderColor: '{primary.color}',
            hoverBorderColor: '{primary.hoverColor}',
            activeBorderColor: '{primary.activeColor}',
            color: '{primary.contrastColor}',
            hoverColor: '{primary.contrastColor}',
            activeColor: '{primary.contrastColor}',
            disabledBackground: '{surface.200}',
            disabledBorderColor: '{surface.200}',
            disabledColor: '{surface.600}'
          },

          // Secondary
          secondary: {
            background: '{surface.0}',
            hoverBackground: '{surface.200}',
            activeBackground: '{surface.300}',
            borderColor: '{primary.color}',
            hoverBorderColor: '{primary.color}',
            activeBorderColor: '{primary.color}',
            color: '{primary.color}',
            hoverColor: '{primary.color}',
            activeColor: '{primary.color}',
            disabledBackground: '{surface.200}',
            disabledBorderColor: '{surface.600}',
            disabledColor: '{surface.600}',
            focusRing: {
              color: '{primary.hoverColor}'
            }
          },

          // Tertiary
          tertiary: {
            background: 'transparent',
            hoverBackground: 'transparent',
            activeBackground: 'transparent',
            borderColor: 'transparent',
            hoverBorderColor: 'transparent',
            activeBorderColor: 'transparent',
            color: '{primary.color}',
            hoverColor: '{primary.hoverColor}',
            activeColor: '{primary.activeColor}',
            disabledBackground: 'transparent',
            disabledBorderColor: 'transparent',
            disabledColor: '{surface.600}',
            focusRing: {
              color: '{primary.hoverColor}',
              shadow: 'none'
            }
          },

          // Success
          success: {
            background: '{success.color}',
            hoverBackground: '{success.hoverColor}',
            activeBackground: '{success.activeColor}',
            borderColor: '{success.color}',
            hoverBorderColor: '{success.hoverColor}',
            activeBorderColor: '{success.activeColor}',
            color: '{success.contrastColor}',
            hoverColor: '{success.contrastColor}',
            activeColor: '{success.contrastColor}',
            focusRing: {
              color: '{success.color}'
            }
          },

          // Menu
          contrast: {
            background: '{surface.1000}',
            hoverBackground: '{surface.900}',
            activeBackground: '{surface.1100}',
            borderColor: '{surface.1000}',
            hoverBorderColor: '{surface.900}',
            activeBorderColor: '{surface.1100}',
            color: '{surface.0}',
            hoverColor: '{surface.200}',
            activeColor: '{surface.0}'
          }
        },

        // Tertiary — PrimeNG p-button-text (mirrors root.tertiary)
        text: {
          primary: {
            hoverBackground: 'transparent',
            activeBackground: 'transparent',
            color: '{primary.color}',
            hoverColor: '{primary.hoverColor}',
            activeColor: '{primary.activeColor}'
          },
          // For table filter 'Add Rule' button
          info: {
            color: '{info.color}',
            hoverColor: '{info.hoverColor}',
            hoverBackground: '{surface.0}'
          }
        },

        // For table filter 'Clear' button
        outlined: {
          primary: {
            hoverBackground: '{surface.200}'
          }
        }
      }
    }
  }
};

export type ButtonTokens = typeof buttonTokens;
